// Ecommerce → Catálogo Web → Colecciones / Home y Banners / Páginas.
// Tres pantallas compactas del CONTENIDO de la tienda (scripts 324/325).
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, FolderTree, Image as ImageIcon, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { Campo, Switch, fieldCls } from '@/features/admin/shared';
import { ColeccionT, ProductoWebT, ecomRpc } from './shared';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';

function useEcomInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['ecom-colecciones'] });
    void qc.invalidateQueries({ queryKey: ['ecom-banners'] });
    void qc.invalidateQueries({ queryKey: ['ecom-paginas'] });
    void qc.invalidateQueries({ queryKey: ['ecom-tienda'] });
  };
}

// ---------------------------------------------------------------------------
export function ColeccionesWeb() {
  const inv = useEcomInvalidate();
  const [sel, setSel] = useState<ColeccionT | null>(null);
  const [nombre, setNombre] = useState('');
  const [productos, setProductos] = useState<string[]>([]);
  const [publicada, setPublicada] = useState(true);

  const { data: colecciones = [] } = useQuery<ColeccionT[]>({
    queryKey: ['ecom-colecciones'],
    queryFn: () => ecomRpc<ColeccionT[]>('ecom_colecciones_listar', {}),
    staleTime: 0, refetchOnMount: 'always',
  });
  const { data: publicados = [] } = useQuery<ProductoWebT[]>({
    queryKey: ['ecom-prods-pub'],
    queryFn: () => ecomRpc<ProductoWebT[]>('ecom_productos_web', { _q: null, _solo_publicados: true }),
  });

  const cargar = (c: ColeccionT | null) => {
    setSel(c); setNombre(c?.nombre ?? ''); setProductos(c?.productos ?? []); setPublicada(c?.publicada ?? true);
  };
  const guardar = async () => {
    try {
      await ecomRpc('ecom_coleccion_guardar', { _data: { nombre, productos, publicada }, _id: sel?.id ?? null });
      inv(); toast.success('Colección guardada'); cargar(null);
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const dlg = useDialogoEnLinea();

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      {dlg.ui}
      <h1 className="text-xl font-semibold flex items-center gap-2"><FolderTree className="w-5 h-5" /> Colecciones de la tienda</h1>
      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="flex gap-1.5 flex-wrap">
            {colecciones.map((c) => (
              <span key={c.id} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-sm cursor-pointer
                  ${sel?.id === c.id ? 'border-primary text-primary font-medium' : ''} ${!c.publicada ? 'opacity-50' : ''}`}
                    onClick={() => cargar(c)}>
                {c.nombre} <span className="text-xs text-muted-foreground">({c.productos.length})</span>
                <button onClick={async (e) => {
                  e.stopPropagation();
                  if (await dlg.confirmar(`¿Eliminar "${c.nombre}"?`, { peligro: true }))
                    void ecomRpc('ecom_coleccion_guardar', { _data: {}, _id: c.id, _eliminar: true })
                      .then(() => { inv(); toast.success('Eliminada'); });
                }}><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
              </span>
            ))}
            <Button size="sm" variant="outline" onClick={() => cargar(null)}><Plus className="w-4 h-4 mr-1" /> Nueva</Button>
          </div>
          <div className="border rounded-md p-3 space-y-3">
            <div className="flex gap-3 items-end flex-wrap">
              <Campo label="Nombre *">
                <input className={`${fieldCls} !w-64`} placeholder="Novedades, Ofertas…" value={nombre}
                       onChange={(e) => setNombre(e.target.value)} />
              </Campo>
              <Switch checked={publicada} onChange={setPublicada} label="Publicada" />
              <Button size="sm" onClick={() => void guardar()} disabled={!nombre.trim()}>Guardar colección</Button>
            </div>
            <div className="text-sm font-medium">Productos ({productos.length} seleccionados)</div>
            <div className="max-h-72 overflow-auto border rounded-md divide-y">
              {publicados.map((p) => (
                <label key={p.producto_id} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer">
                  <input type="checkbox" checked={productos.includes(p.producto_id)}
                         onChange={(e) => setProductos((xs) => e.target.checked
                           ? [...xs, p.producto_id] : xs.filter((x) => x !== p.producto_id))} />
                  {p.titulo ?? p.nombre}
                </label>
              ))}
              {!publicados.length && <p className="px-3 py-4 text-sm text-muted-foreground">Primero publica productos en Publicación de Productos.</p>}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
interface BannerT {
  id: string; titulo: string | null; subtitulo: string | null; imagen: string | null;
  enlace: string | null; orden: number; desde: string | null; hasta: string | null; activo: boolean;
}
export function BannersWeb() {
  const qc = useQueryClient();
  const [f, setF] = useState({ titulo: '', subtitulo: '', imagen: '', enlace: '', desde: '', hasta: '' });
  const [editId, setEditId] = useState<string | null>(null);

  const { data: banners = [] } = useQuery<BannerT[]>({
    queryKey: ['ecom-banners'],
    queryFn: async () => (await apiClient.get('/rest/ecommerce.banners?select=*&deleted_at=is.null&order=orden')).data,
    staleTime: 0, refetchOnMount: 'always',
  });

  const guardar = async () => {
    try {
      await ecomRpc('ecom_banner_guardar', { _data: { ...f }, _id: editId });
      void qc.invalidateQueries({ queryKey: ['ecom-banners'] });
      toast.success('Banner guardado'); setEditId(null);
      setF({ titulo: '', subtitulo: '', imagen: '', enlace: '', desde: '', hasta: '' });
    } catch (e) { toast.error(getErrorMessage(e)); }
  };
  const leerImagen = (file: File | null) => {
    if (!file) return;
    const r = new FileReader();
    r.onload = () => setF((x) => ({ ...x, imagen: String(r.result) }));
    r.readAsDataURL(file);
  };

  const dlg = useDialogoEnLinea();

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      {dlg.ui}
      <h1 className="text-xl font-semibold flex items-center gap-2"><ImageIcon className="w-5 h-5" /> Home y Banners</h1>
      <Card>
        <CardContent className="pt-4 space-y-3">
          {banners.map((b) => (
            <div key={b.id} className="border rounded-md p-2 flex items-center gap-3 text-sm">
              {b.imagen && <img src={b.imagen} alt="" className="w-24 h-12 object-cover rounded" />}
              <div className="flex-1">
                <div className="font-medium">{b.titulo ?? '(sin título)'}</div>
                <div className="text-xs text-muted-foreground">
                  {b.subtitulo ?? ''} {b.enlace ? `→ ${b.enlace}` : ''} {b.desde || b.hasta ? `· ${b.desde ?? ''} a ${b.hasta ?? ''}` : ''}
                </div>
              </div>
              <Switch checked={b.activo} label=""
                      onChange={(v) => void ecomRpc('ecom_banner_guardar', { _data: { activo: v }, _id: b.id })
                        .then(() => qc.invalidateQueries({ queryKey: ['ecom-banners'] }))} />
              <Button size="sm" variant="ghost" onClick={() => {
                setEditId(b.id);
                setF({ titulo: b.titulo ?? '', subtitulo: b.subtitulo ?? '', imagen: '', enlace: b.enlace ?? '',
                  desde: b.desde ?? '', hasta: b.hasta ?? '' });
              }}>✎</Button>
              <Button size="sm" variant="ghost" onClick={async () => {
                if (await dlg.confirmar('¿Eliminar el banner?', { peligro: true }))
                  void ecomRpc('ecom_banner_guardar', { _data: {}, _id: b.id, _eliminar: true })
                    .then(() => qc.invalidateQueries({ queryKey: ['ecom-banners'] }));
              }}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          ))}
          <div className="border rounded-md p-3 grid gap-3 md:grid-cols-3">
            <Campo label={editId ? 'Editar banner' : 'Nuevo banner — título'}>
              <input className={fieldCls} value={f.titulo} onChange={(e) => setF({ ...f, titulo: e.target.value })} />
            </Campo>
            <Campo label="Subtítulo">
              <input className={fieldCls} value={f.subtitulo} onChange={(e) => setF({ ...f, subtitulo: e.target.value })} />
            </Campo>
            <Campo label="Enlace (slug de colección o URL)">
              <input className={fieldCls} placeholder="ofertas" value={f.enlace} onChange={(e) => setF({ ...f, enlace: e.target.value })} />
            </Campo>
            <Campo label={`Imagen ${editId ? '(vacía = conservar)' : ''}`}>
              <input className={fieldCls} type="file" accept="image/*" onChange={(e) => leerImagen(e.target.files?.[0] ?? null)} />
            </Campo>
            <Campo label="Vigente desde">
              <input className={fieldCls} type="date" value={f.desde} onChange={(e) => setF({ ...f, desde: e.target.value })} />
            </Campo>
            <Campo label="Hasta">
              <input className={fieldCls} type="date" value={f.hasta} onChange={(e) => setF({ ...f, hasta: e.target.value })} />
            </Campo>
            <div className="md:col-span-3 flex justify-end gap-2">
              {editId && <Button size="sm" variant="outline" onClick={() => { setEditId(null); setF({ titulo: '', subtitulo: '', imagen: '', enlace: '', desde: '', hasta: '' }); }}>Cancelar</Button>}
              <Button size="sm" onClick={() => void guardar()}>Guardar banner</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
interface PaginaT { id: string; slug: string; titulo: string; contenido: string | null; publicada: boolean }
const PLANTILLAS = [
  { titulo: 'Política de devoluciones', contenido: 'Aceptamos devoluciones dentro de los 3 días posteriores a la entrega, con el producto sin uso y en su empaque original. El reembolso se procesa como nota de crédito o al mismo medio de pago.\n\nPara iniciar una devolución escríbenos por WhatsApp con tu número de pedido.' },
  { titulo: 'Política de privacidad', contenido: 'Tratamos tus datos personales (nombre, contacto y dirección) únicamente para procesar tu pedido y facturarlo, conforme a la Ley Orgánica de Protección de Datos Personales del Ecuador. No compartimos tus datos con terceros salvo el courier de entrega. Puedes ejercer tus derechos de acceso, rectificación y eliminación escribiéndonos.' },
  { titulo: 'Términos y condiciones', contenido: 'Los precios incluyen impuestos según se detalla en el checkout. Las fotos son referenciales. La disponibilidad está sujeta a stock al momento de confirmar el pago. La factura electrónica se emite con los datos proporcionados en el pedido.' },
];
export function PaginasWeb() {
  const qc = useQueryClient();
  const [sel, setSel] = useState<PaginaT | null>(null);
  const [titulo, setTitulo] = useState('');
  const [contenido, setContenido] = useState('');
  const { data: paginas = [] } = useQuery<PaginaT[]>({
    queryKey: ['ecom-paginas'],
    queryFn: async () => (await apiClient.get('/rest/ecommerce.paginas?select=*&deleted_at=is.null&order=titulo')).data,
    staleTime: 0, refetchOnMount: 'always',
  });
  const guardar = async () => {
    try {
      await ecomRpc('ecom_pagina_guardar', { _data: { titulo, contenido }, _id: sel?.id ?? null });
      void qc.invalidateQueries({ queryKey: ['ecom-paginas'] });
      toast.success('Página guardada'); setSel(null); setTitulo(''); setContenido('');
    } catch (e) { toast.error(getErrorMessage(e)); }
  };
  const dlg = useDialogoEnLinea();

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      {dlg.ui}
      <h1 className="text-xl font-semibold flex items-center gap-2"><FileText className="w-5 h-5" /> Páginas de la tienda</h1>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Políticas y contenido (obligatorias para vender en línea)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-1.5 flex-wrap">
            {paginas.map((p) => (
              <span key={p.id} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-sm cursor-pointer
                  ${sel?.id === p.id ? 'border-primary text-primary font-medium' : ''}`}
                    onClick={() => { setSel(p); setTitulo(p.titulo); setContenido(p.contenido ?? ''); }}>
                {p.titulo}
                <button onClick={async (e) => {
                  e.stopPropagation();
                  if (await dlg.confirmar(`¿Eliminar "${p.titulo}"?`, { peligro: true }))
                    void ecomRpc('ecom_pagina_guardar', { _data: {}, _id: p.id, _eliminar: true })
                      .then(() => qc.invalidateQueries({ queryKey: ['ecom-paginas'] }));
                }}><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
              </span>
            ))}
            {PLANTILLAS.filter((t) => !paginas.some((p) => p.titulo === t.titulo)).map((t) => (
              <Button key={t.titulo} size="sm" variant="outline"
                      onClick={() => { setSel(null); setTitulo(t.titulo); setContenido(t.contenido); }}>
                <Plus className="w-3.5 h-3.5 mr-1" /> {t.titulo}
              </Button>
            ))}
          </div>
          <Campo label="Título">
            <input className={fieldCls} value={titulo} onChange={(e) => setTitulo(e.target.value)} />
          </Campo>
          <Campo label="Contenido">
            <textarea className={fieldCls} rows={10} value={contenido} onChange={(e) => setContenido(e.target.value)} />
          </Campo>
          <div className="flex justify-end">
            <Button size="sm" onClick={() => void guardar()} disabled={!titulo.trim()}>Guardar página</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
